import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
    Target, Plus, FileText, Trash2, Edit, LogOut, 
    Gamepad2, Calendar, MoreVertical, Loader2, Crown, Zap
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, logout, getAuthHeaders, refreshUser, userTier } = useAuth();
    const [resumes, setResumes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [resumeToDelete, setResumeToDelete] = useState(null);

    const fetchResumes = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/resumes`, getAuthHeaders());
            setResumes(response.data);
        } catch (error) {
            toast.error('Failed to load resumes');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        // Check for successful subscription
        const checkSubscription = async () => {
            const sessionId = searchParams.get('session_id');
            const subscription = searchParams.get('subscription');
            
            if (sessionId && subscription === 'success') {
                try {
                    const response = await axios.get(`${API}/subscribe/status/${sessionId}`, getAuthHeaders());
                    if (response.data.status === 'paid') {
                        await refreshUser();
                        toast.success(`Upgraded to ${response.data.tier}! Enjoy your new features.`);
                    }
                } catch (error) {
                    toast.error('Could not verify subscription status');
                }
                // Clean URL
                window.history.replaceState({}, '', '/dashboard');
            }
        };
        
        checkSubscription();
        fetchResumes();
    }, [searchParams, getAuthHeaders, refreshUser, fetchResumes]);

    const handleDelete = async () => {
        if (!resumeToDelete) return;
        
        try {
            await axios.delete(`${API}/resumes/${resumeToDelete}`, getAuthHeaders());
            setResumes(resumes.filter(r => r.id !== resumeToDelete));
            toast.success('Resume deleted');
        } catch (error) {
            toast.error('Failed to delete resume');
        } finally {
            setDeleteDialogOpen(false);
            setResumeToDelete(null);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-[#09090b]">
            {/* Header */}
            <header className="border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Target className="w-6 h-6 text-primary" />
                        <span className="font-bold text-lg tracking-tight">LevelUp Resume</span>
                    </Link>
                    
                    <div className="flex items-center gap-4">
                        {/* Tier Badge */}
                        {userTier === 'free' ? (
                            <Link 
                                to="/pricing"
                                className="hidden sm:flex items-center gap-1 px-3 py-1 bg-secondary rounded-full text-xs hover:bg-secondary/80 transition-colors"
                            >
                                <Zap className="w-3 h-3" />
                                Upgrade
                            </Link>
                        ) : (
                            <span className={`hidden sm:flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                                userTier === 'premium' 
                                    ? 'bg-yellow-500/20 text-yellow-400' 
                                    : 'bg-primary/20 text-primary'
                            }`}>
                                <Crown className="w-3 h-3" />
                                {userTier.toUpperCase()}
                            </span>
                        )}
                        <span className="text-sm text-muted-foreground hidden sm:block">
                            Welcome, <span className="text-foreground font-medium">{user?.username}</span>
                        </span>
                        <Button 
                            data-testid="logout-btn"
                            variant="ghost" 
                            size="sm" 
                            onClick={handleLogout}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Your Resumes</h1>
                        <p className="text-muted-foreground mt-1">Manage and create gaming-powered resumes</p>
                    </div>
                    <Button 
                        data-testid="create-resume-btn"
                        onClick={() => navigate('/builder')}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider btn-glow"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Resume
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : resumes.length === 0 ? (
                    <div className="glass rounded-xl p-12 text-center">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                            <Gamepad2 className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold mb-2">No Resumes Yet</h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Create your first resume by adding your gaming experience and let AI transform it into professional skills.
                        </p>
                        <Button 
                            data-testid="create-first-resume-btn"
                            onClick={() => navigate('/builder')}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider btn-glow"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Resume
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {resumes.map((resume, index) => (
                            <div 
                                key={resume.id}
                                data-testid={`resume-card-${index}`}
                                className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-300 animate-fadeIn"
                                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-2 bg-primary/20 rounded-lg">
                                        <FileText className="w-5 h-5 text-primary" />
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => navigate(`/builder/${resume.id}`)}>
                                                <Edit className="w-4 h-4 mr-2" />
                                                Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => {
                                                    setResumeToDelete(resume.id);
                                                    setDeleteDialogOpen(true);
                                                }}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                                
                                <h3 className="font-bold text-lg mb-1 line-clamp-1">{resume.title}</h3>
                                {resume.target_job && (
                                    <p className="text-sm text-primary mb-3">{resume.target_job}</p>
                                )}
                                
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                                    <span className="flex items-center gap-1">
                                        <Gamepad2 className="w-4 h-4" />
                                        {resume.games?.length || 0} games
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        {formatDate(resume.updated_at)}
                                    </span>
                                </div>
                                
                                <Button 
                                    data-testid={`view-resume-btn-${index}`}
                                    onClick={() => navigate(`/builder/${resume.id}`)}
                                    variant="outline"
                                    className="w-full border-white/10 hover:bg-white/5"
                                >
                                    View Resume
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Resume?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your resume.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
